import { useMemo, useState } from 'react';

import styled, { css } from 'styled-components';
import {
	CONTENT_TYPE_MAP,
	PLATFORM_TYPE_MAP,
	IContentRankingDB,
	IRankingFilterParams,
} from 'type/tracking';

import TableCell from 'components/common/table/cell/TableCell';
import SimpleTableFooter from 'components/SimpleTableFooter';
import { useGetContentRanking, useGetInfluencers } from 'hooks/queries/tracking';
import CoreImg from 'shared/CoreImg';
import CoreLoading from 'shared/CoreLoading';
import CoreSelect from 'shared/CoreSelect';
import CoreSvg from 'shared/CoreSvg';
import CoreTooltip from 'shared/CoreTooltip';
import Icon from 'shared/icons';
import style from 'styles/styled-components/style';

const CONTENT_TYPE_ICON: Record<string, JSX.Element> = {
	ig_feed: <Icon.Insta />,
	ig_video: <Icon.InstaReels />,
	yt_video: <Icon.Youtube />,
	yt_shorts: <Icon.YoutubeShorts />,
};

const PLATFORM_ICON_MAP: Record<string, JSX.Element> = {
	INSTAGRAM: <Icon.Insta />,
	YOUTUBE: <Icon.Youtube />,
	TIKTOK: <Icon.Tiktok />,
	THREADS: <Icon.Insta />,
};

// API orderBy 매핑
const ORDER_BY_MAP: Record<string, Record<string, string>> = {
	viewCount: { desc: 'viewDesc', asc: 'viewAsc' },
	likeCount: { desc: 'likeDesc', asc: 'likeAsc' },
	commentCount: { desc: 'commentDesc', asc: 'commentAsc' },
	shareCount: { desc: 'shareDesc', asc: 'shareAsc' },
	followers: { desc: 'followerDesc', asc: 'followerAsc' },
};

interface IContentRankingTable {
	code: string;
	filter: IRankingFilterParams;
}

const PAGE_SIZE_OPTIONS = ['25개씩 보기', '50개씩 보기', '100개씩 보기'];
const PAGE_SIZE_MAP: Record<string, number> = {
	'25개씩 보기': 25,
	'50개씩 보기': 50,
	'100개씩 보기': 100,
};
const PAGE_SIZE_REVERSE: Record<number, string> = {
	25: '25개씩 보기',
	50: '50개씩 보기',
	100: '100개씩 보기',
};

const formatDate = (dateStr: string) => {
	if (!dateStr) return '';
	const d = new Date(dateStr);
	if (isNaN(d.getTime())) return '';
	const yy = String(d.getFullYear()).slice(2);
	const mm = String(d.getMonth() + 1).padStart(2, '0');
	const dd = String(d.getDate()).padStart(2, '0');
	return `${yy}.${mm}.${dd}`;
};

const COLUMN_WIDTHS = {
	rank: 60,
	content: 310,
	creator: 140,
	followers: 100,
	viewCount: 100,
	likeCount: 100,
	commentCount: 100,
	shareCount: 100,
};

type FlexType = 'start' | 'center' | 'end' | 'between';

const HEADER_ITEMS: {
	header: string;
	name: string;
	width: number;
	align: FlexType;
	sortable?: boolean;
}[] = [
	{ header: '순위', name: 'rank', width: COLUMN_WIDTHS.rank, align: 'center' },
	{
		header: '콘텐츠',
		name: 'content',
		width: COLUMN_WIDTHS.content,
		align: 'start',
	},
	{
		header: '계정/채널',
		name: 'creator',
		width: COLUMN_WIDTHS.creator,
		align: 'start',
	},
	{
		header: '팔로워 수',
		name: 'followers',
		width: COLUMN_WIDTHS.followers,
		align: 'end',
		sortable: true,
	},
	{
		header: '조회수',
		name: 'viewCount',
		width: COLUMN_WIDTHS.viewCount,
		align: 'end',
		sortable: true,
	},
	{
		header: '좋아요',
		name: 'likeCount',
		width: COLUMN_WIDTHS.likeCount,
		align: 'end',
		sortable: true,
	},
	{
		header: '댓글',
		name: 'commentCount',
		width: COLUMN_WIDTHS.commentCount,
		align: 'end',
		sortable: true,
	},
	{
		header: '공유',
		name: 'shareCount',
		width: COLUMN_WIDTHS.shareCount,
		align: 'end',
		sortable: true,
	},
];

const ContentRankingTable = ({ code, filter }: IContentRankingTable) => {
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(25);
	const [sortField, setSortField] = useState('viewCount');
	const [sortDirection, setSortDirection] = useState<'desc' | 'asc'>('desc');

	const { data: influencers } = useGetInfluencers(code);
	const influencerPkMap = useMemo(
		() => new Map(influencers?.map((i) => [i.influencerCode, i.accountCode]) ?? []),
		[influencers],
	);

	const apiOrderBy = ORDER_BY_MAP[sortField]?.[sortDirection] ?? 'viewDesc';

	const params: IRankingFilterParams = {
		...filter,
		orderBy: apiOrderBy,
		page,
		pageSize,
	};

	const { data: response, isLoading } = useGetContentRanking(code, params);

	const handleSort = (field: string) => {
		if (sortField === field) {
			if (sortDirection === 'desc') {
				setSortDirection('asc');
			} else {
				// asc 상태에서 재클릭 → 기본 정렬로 복귀
				setSortField('viewCount');
				setSortDirection('desc');
			}
		} else {
			setSortField(field);
			setSortDirection('desc');
		}
		setPage(1);
	};

	const handlePageSizeChange = (option: string) => {
		setPageSize(PAGE_SIZE_MAP[option] ?? 25);
		setPage(1);
	};

	const handleRowClick = (url: string) => {
		window.open(url, '_blank', 'noopener,noreferrer');
	};

	const handleCreatorClick = (item: IContentRankingDB, e: React.MouseEvent) => {
		e.stopPropagation();
		const platform = (item.platformType ?? '').toLowerCase();
		const pk = influencerPkMap.get(item.influencerCode) ?? item.username;
		const reportUrl = `https://app.featuring.co/report/${platform}/${pk}`;
		window.open(reportUrl, '_blank', 'noopener,noreferrer');
	};

	const getSortIndicator = (field: string) => {
		if (sortField === field) {
			return sortDirection === 'desc' ? ' ↓' : ' ↑';
		}
		return '';
	};

	const topCount = response?.totalCount ?? pageSize;

	return (
		<div>
			<Grid flex="between" align_items="end" _margin="16px 0 8px 0">
				<Text typo="p_bold">{`전체 콘텐츠 ${(topCount).toLocaleString()}개`}</Text>
				<Grid flex="end" _gap="8px" _width="auto">
					<CoreSelect
						select_type="box"
						_width="160px"
						_height="36px"
						option_list={PAGE_SIZE_OPTIONS}
						selected_option={PAGE_SIZE_REVERSE[pageSize]}
						setSelectedOption={handlePageSizeChange}
					/>
				</Grid>
			</Grid>

			<TableHeaderWrapper>
				{HEADER_ITEMS.map((item, idx) => (
					<>
						<HeaderCell
							key={item.name}
							_width={`${item.width}px`}
							flex={item.align ?? 'center'}
							align="center"
							_padding="0 10px"
							$sortable={!!item.sortable}
							onClick={item.sortable ? () => handleSort(item.name) : undefined}
						>
							<Text white_space="pre" typo="span">
								{item.header}
								{item.sortable && getSortIndicator(item.name)}
							</Text>
						</HeaderCell>
						{idx !== HEADER_ITEMS.length - 1 && idx !== 0 && (
							<Contour _height="40%" _width="1px" />
						)}
					</>
				))}
			</TableHeaderWrapper>

			{isLoading ? (
				<Grid flex="center" _height="500px">
					<CoreLoading color_type="black" size_type="large" />
				</Grid>
			) : !response?.contents?.length ? (
				<Grid flex="center" _height="500px">
					<Text typo="p" color="gray">
						데이터가 없습니다.
					</Text>
				</Grid>
			) : (
				response.contents.map((item: IContentRankingDB) => (
					<TableRow
						key={`${item.contentLink}-${item.rank}`}
						selected={false}
						onClick={() => handleRowClick(item.contentLink)}
					>
						<TableCell cell_type="center" _width={COLUMN_WIDTHS.rank}>
							{String(item.rank)}
						</TableCell>
						<TableCell
							cell_type="common"
							_width={COLUMN_WIDTHS.content}
							_padding="0 10px"
						>
							<ContentCell>
								<ContentThumbnail>
									{CONTENT_TYPE_ICON[item.contentType] && (
										<ContentTypeIcon>
											<CoreSvg _size="12px">
												{CONTENT_TYPE_ICON[item.contentType]}
											</CoreSvg>
										</ContentTypeIcon>
									)}
									<CoreImg
										src={item.thumbnailUrl}
										alt=""
										_width={56}
										_height={56}
										error_src="https://featuring-score-web.s3.amazonaws.com/static/images/score/v3/common/cmmn-default-image.png"
										lazy
									/>
								</ContentThumbnail>
								<ContentInfo>
									<ContentDesc>{item.contentTitle ?? item.contentDescription}</ContentDesc>
									<ContentDate>{formatDate(item.uploadDate)}</ContentDate>
								</ContentInfo>
							</ContentCell>
						</TableCell>
						<TableCell
							cell_type="common"
							_width={COLUMN_WIDTHS.creator}
							_padding="0 10px"
						>
							<CoreTooltip direction="down" text="계정 리포트 바로가기">
								<CreatorCell onClick={(e) => handleCreatorClick(item, e)}>
									<AvatarWithBadge>
										<AvatarRound>
											<CoreImg
												src={item.profileImageUrl}
												alt=""
												_width={28}
												_height={28}
												error_src="https://featuring-score-web.s3.amazonaws.com/static/images/score/v3/common/cmmn-default-image.png"
												lazy
											/>
										</AvatarRound>
										{PLATFORM_ICON_MAP[item.platformType] && (
											<PlatformBadge>
												<CoreSvg _size="10px">{PLATFORM_ICON_MAP[item.platformType]}</CoreSvg>
											</PlatformBadge>
										)}
									</AvatarWithBadge>
									<CreatorInfo>
										<CreatorName>{item.username}</CreatorName>
									</CreatorInfo>
								</CreatorCell>
							</CoreTooltip>
						</TableCell>
						<TableCell cell_type="number" _width={COLUMN_WIDTHS.followers}>
							{(item.followerCount ?? 0).toLocaleString()}
						</TableCell>
						<TableCell cell_type="number" _width={COLUMN_WIDTHS.viewCount}>
							{item.viewCountsDisabled ? '비공개' : (item.viewCount ?? 0).toLocaleString()}
						</TableCell>
						<TableCell cell_type="number" _width={COLUMN_WIDTHS.likeCount}>
							{item.likeCountsDisabled ? '비공개' : (item.likeCount ?? 0).toLocaleString()}
						</TableCell>
						<TableCell cell_type="number" _width={COLUMN_WIDTHS.commentCount}>
							{item.commentsDisabled ? '비공개' : (item.commentCount ?? 0).toLocaleString()}
						</TableCell>
						<TableCell cell_type="number" _width={COLUMN_WIDTHS.shareCount}>
							{(item.shareCount ?? 0).toLocaleString()}
						</TableCell>
					</TableRow>
				))
			)}

			{response && response.totalCount > 0 && (
				<FooterWrapper>
					<SimpleTableFooter
						data={Array(response.totalCount).fill(null) as null[]}
						page={page}
						filter_page={pageSize}
						setPage={setPage}
					/>
				</FooterWrapper>
			)}
		</div>
	);
};

const { Grid, Text } = style;

const TableHeaderWrapper = styled(Grid)`
	height: 66px;
	background-color: ${({ theme }) => theme.colors.gray.gray_white};
	border-bottom: 1px solid ${({ theme }) => theme.colors.gray.gray15};
	border-top-left-radius: 8px;
	border-top-right-radius: 8px;
	padding: 0 16px;

	${({ theme }) => theme.common.flexStart};
`;

const HeaderCell = styled(Grid)<{ $sortable: boolean }>`
	padding: 8px 10px;
	${({ $sortable }) =>
		$sortable &&
		css`
			cursor: pointer;
			&:hover {
				background-color: ${({ theme }) => theme.colors.gray.gray05_bg};
				border-radius: 4px;
			}
			&:hover span {
				color: ${({ theme }) => theme.colors.gray.gray80_text};
			}
		`}
`;

const TableRow = styled(Grid)<{ selected: boolean }>`
	min-width: calc(100% - 32px);
	height: 72px;

	border-radius: 8px;
	margin-top: 8px;
	padding: 0 16px;
	cursor: pointer;

	${({ theme }) => theme.common.flexStart}

	background-color: ${({ theme }) => theme.colors.gray.gray_white};
	border: 1px solid ${({ theme }) => theme.colors.gray.gray_white};

	${({ theme, selected }) =>
		selected &&
		css`
			border: 1px solid ${theme.colors.uplus.primary10_lighten};
		`}

	&:hover {
		border-color: ${({ theme }) => theme.colors.uplus.bg10_lighten};
		background-color: ${({ theme }) => theme.colors.uplus.bg10_lighten};
	}
`;

const Contour = styled(Grid)`
	background-color: ${({ theme }) => theme.colors.gray.gray15};
`;

const ContentCell = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	width: 100%;
`;

const ContentThumbnail = styled.div`
	position: relative;
	width: 56px;
	height: 56px;
	border-radius: 6px;
	overflow: hidden;
	flex-shrink: 0;
`;

const ContentTypeIcon = styled.div`
	position: absolute;
	top: 4px;
	left: 4px;
	width: 20px;
	height: 20px;
	border-radius: 4px;
	background: rgba(255, 255, 255, 0.9);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1;
`;

const ContentInfo = styled.div`
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 2px;
`;

const ContentTypeLine = styled.div`
	${({ theme }) => theme.typo.body.span};
	color: ${({ theme }) => theme.colors.gray.gray50};
`;

const ContentDesc = styled.div`
	${({ theme }) => theme.typo.body.span};
	color: ${({ theme }) => theme.colors.gray.gray80_text};
	${({ theme }) => theme.common.ellipsis};
`;

const ContentDate = styled.div`
	${({ theme }) => theme.typo.body.span};
	color: ${({ theme }) => theme.colors.gray.gray50};
`;

const CreatorCell = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 4px 6px;
	border-radius: 6px;
	cursor: pointer;

	&:hover {
		background-color: ${({ theme }) => theme.colors.gray.gray05_bg};
	}
`;

const AvatarWithBadge = styled.div`
	position: relative;
	width: 28px;
	height: 28px;
	flex-shrink: 0;
`;

const AvatarRound = styled.div`
	width: 28px;
	height: 28px;
	border-radius: 50%;
	overflow: hidden;
`;

const PlatformBadge = styled.div`
	position: absolute;
	bottom: -2px;
	right: -2px;
	width: 14px;
	height: 14px;
	border-radius: 50%;
	background: white;
	display: flex;
	align-items: center;
	justify-content: center;
	border: 1px solid ${({ theme }) => theme.colors.gray.gray15};
`;

const CreatorInfo = styled.div`
	display: flex;
	flex-direction: column;
	min-width: 0;
`;

const CreatorName = styled.span`
	${({ theme }) => theme.typo.body.span_bold};
	color: ${({ theme }) => theme.colors.gray.gray80_text};
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;

	&:hover {
		text-decoration: underline;
	}
`;

const FooterWrapper = styled.div`
	margin-top: 16px;
`;

export default ContentRankingTable;
